// pages/notifications/NotificationSettings.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Switch,
  FormControlLabel,
  FormGroup,
  Divider,
  Button,
  TextField,
  Alert,
  Snackbar,
  Chip,
  Stack
} from '@mui/material';
import {
  Notifications,
  Email,
  PhoneAndroid,
  Sms,
  Schedule,
  VolumeUp,
  VolumeOff,
  Save,
  Restore,
  NotificationsActive
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { LocalizationProvider, TimePicker as MuiTimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import apiClient from '../../../../api/client';

const NotificationSettings = () => {
  const { t } = useTranslation();
  const MotionDiv = motion.div;
  
  // State for notification preferences
  const [preferences, setPreferences] = useState({
    // Channel preferences
    realtime_enabled: true,
    email_enabled: true,
    push_enabled: true,
    sms_enabled: false,
    
    // Category preferences
    user_management_enabled: true,
    content_management_enabled: true,
    vendor_management_enabled: true,
    system_notifications_enabled: true,
    support_notifications_enabled: true,
    social_media_enabled: true,
    news_updates_enabled: true,
    
    // Priority filters
    high_priority_only: false,
    quiet_hours_start: null,
    quiet_hours_end: null,
    weekend_notifications: true,
    
    // Digest settings
    daily_digest_enabled: false,
    weekly_digest_enabled: false,
    digest_time: new Date().setHours(9, 0, 0, 0)
  });
  
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  
  // Load current preferences with useCallback to fix dependency warning
  const loadPreferences = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/v1/notifications/preferences');
      if (response.data) {
        setPreferences(prev => ({
          ...prev,
          ...response.data,
          quiet_hours_start: response.data.quiet_hours_start ? 
            new Date(`2000-01-01T${response.data.quiet_hours_start}`) : null,
          quiet_hours_end: response.data.quiet_hours_end ? 
            new Date(`2000-01-01T${response.data.quiet_hours_end}`) : null,
          digest_time: response.data.digest_time ? 
            new Date(`2000-01-01T${response.data.digest_time}`) : new Date().setHours(9, 0, 0, 0)
        }));
      }
      
      // Load sound preference from localStorage
      const soundPref = localStorage.getItem('notificationSound');
      setSoundEnabled(soundPref !== 'disabled');
    } catch (err) {
      setError(t('notifications.settings.loadError'));
      console.error('Error loading notification preferences:', err);
    }
  }, [t]);
  
  useEffect(() => {
    loadPreferences();
  }, [loadPreferences]);
  
  // Handle preference changes
  const handlePreferenceChange = (key, value) => {
    setPreferences(prev => ({
      ...prev,
      [key]: value
    }));
    setHasChanges(true);
  };
  
  // Handle sound toggle
  const handleSoundToggle = () => {
    const newState = !soundEnabled;
    setSoundEnabled(newState);
    localStorage.setItem('notificationSound', newState ? 'enabled' : 'disabled');
  };
  
  // Save preferences
  const savePreferences = async () => {
    setSaving(true);
    try {
      const dataToSave = {
        ...preferences,
        quiet_hours_start: preferences.quiet_hours_start ? 
          preferences.quiet_hours_start.toTimeString().slice(0, 8) : null,
        quiet_hours_end: preferences.quiet_hours_end ? 
          preferences.quiet_hours_end.toTimeString().slice(0, 8) : null,
        digest_time: preferences.digest_time ? 
          new Date(preferences.digest_time).toTimeString().slice(0, 8) : '09:00:00'
      };
      
      await apiClient.put('/api/v1/notifications/preferences', dataToSave);
      setSuccess(true);
      setHasChanges(false);
    } catch (err) {
      setError(t('notifications.settings.saveError'));
      console.error('Error saving notification preferences:', err);
    } finally {
      setSaving(false);
    }
  };
  
  // Reset to defaults
  const resetToDefaults = () => {
    setPreferences({
      realtime_enabled: true,
      email_enabled: true,
      push_enabled: true,
      sms_enabled: false,
      user_management_enabled: true,
      content_management_enabled: true,
      vendor_management_enabled: true,
      system_notifications_enabled: true,
      support_notifications_enabled: true,
      social_media_enabled: true,
      news_updates_enabled: true,
      high_priority_only: false,
      quiet_hours_start: null,
      quiet_hours_end: null,
      weekend_notifications: true,
      daily_digest_enabled: false,
      weekly_digest_enabled: false,
      digest_time: new Date().setHours(9, 0, 0, 0)
    });
    setHasChanges(true);
  };
  
  // Request notification permission
  const requestNotificationPermission = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        setSuccess(true);
      }
    }
  };
  
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ pb: 6 }}>
        <MotionDiv
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                {t('notifications.settings.title')}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {t('notifications.settings.subtitle')}
              </Typography>
            </Box>
            
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                startIcon={<Restore />}
                onClick={resetToDefaults}
                disabled={saving}
              >
                {t('notifications.settings.resetDefaults')}
              </Button>
              
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={savePreferences}
                disabled={!hasChanges || saving}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5a71d6 0%, #6a4292 100%)'
                  }
                }}
              >
                {saving ? t('common.saving') : t('common.save')}
              </Button>
            </Stack>
          </Box>
        </MotionDiv>
        
        <Grid container spacing={3}>
          {/* Delivery Channels */}
          <Grid item xs={12} md={6}>
            <MotionDiv
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Card>
                <CardHeader
                  avatar={<Notifications color="primary" />}
                  title={t('notifications.settings.deliveryChannels')}
                  subheader={t('notifications.settings.deliveryChannelsDesc')}
                />
                <CardContent>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={preferences.realtime_enabled}
                          onChange={(e) => handlePreferenceChange('realtime_enabled', e.target.checked)}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <NotificationsActive fontSize="small" />
                          {t('notifications.settings.realtime')}
                        </Box>
                      }
                    />
                    
                    <FormControlLabel
                      control={
                        <Switch
                          checked={preferences.email_enabled}
                          onChange={(e) => handlePreferenceChange('email_enabled', e.target.checked)}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Email fontSize="small" />
                          {t('notifications.settings.email')}
                        </Box>
                      }
                    />
                    
                    <FormControlLabel
                      control={
                        <Switch
                          checked={preferences.push_enabled}
                          onChange={(e) => handlePreferenceChange('push_enabled', e.target.checked)}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PhoneAndroid fontSize="small" />
                          {t('notifications.settings.push')}
                          <Chip label="Future" size="small" variant="outlined" color="info" />
                        </Box>
                      }
                    />
                    
                    <FormControlLabel
                      control={
                        <Switch
                          checked={preferences.sms_enabled}
                          onChange={(e) => handlePreferenceChange('sms_enabled', e.target.checked)}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Sms fontSize="small" />
                          {t('notifications.settings.sms')}
                          <Chip label="Future" size="small" variant="outlined" color="info" />
                        </Box>
                      }
                    />
                  </FormGroup>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={soundEnabled}
                        onChange={handleSoundToggle}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {soundEnabled ? <VolumeUp fontSize="small" /> : <VolumeOff fontSize="small" />}
                        {t('notifications.settings.sound')}
                      </Box>
                    }
                  />
                  
                  {Notification.permission === 'default' && (
                    <Box sx={{ mt: 2 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={requestNotificationPermission}
                        startIcon={<NotificationsActive />}
                      >
                        {t('notifications.settings.enableBrowser')}
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </MotionDiv>
          </Grid>
          
          {/* Category Preferences */}
          <Grid item xs={12} md={6}>
            <MotionDiv
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Card>
                <CardHeader
                  title={t('notifications.settings.categories')}
                  subheader={t('notifications.settings.categoriesDesc')}
                />
                <CardContent>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={preferences.user_management_enabled}
                          onChange={(e) => handlePreferenceChange('user_management_enabled', e.target.checked)}
                        />
                      }
                      label={t('notifications.categories.userManagement')}
                    />
                    
                    <FormControlLabel
                      control={
                        <Switch
                          checked={preferences.content_management_enabled}
                          onChange={(e) => handlePreferenceChange('content_management_enabled', e.target.checked)}
                        />
                      }
                      label={t('notifications.categories.contentManagement')}
                    />
                    
                    <FormControlLabel
                      control={
                        <Switch
                          checked={preferences.vendor_management_enabled}
                          onChange={(e) => handlePreferenceChange('vendor_management_enabled', e.target.checked)}
                        />
                      }
                      label={t('notifications.categories.vendorManagement')}
                    />
                    
                    <FormControlLabel
                      control={
                        <Switch
                          checked={preferences.system_notifications_enabled}
                          onChange={(e) => handlePreferenceChange('system_notifications_enabled', e.target.checked)}
                        />
                      }
                      label={t('notifications.categories.system')}
                    />
                    
                    <FormControlLabel
                      control={
                        <Switch
                          checked={preferences.support_notifications_enabled}
                          onChange={(e) => handlePreferenceChange('support_notifications_enabled', e.target.checked)}
                        />
                      }
                      label={t('notifications.categories.support')}
                    />
                    
                    <FormControlLabel
                      control={
                        <Switch
                          checked={preferences.social_media_enabled}
                          onChange={(e) => handlePreferenceChange('social_media_enabled', e.target.checked)}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {t('notifications.categories.socialMedia')}
                          <Chip label="Future" size="small" variant="outlined" color="info" />
                        </Box>
                      }
                    />
                    
                    <FormControlLabel
                      control={
                        <Switch
                          checked={preferences.news_updates_enabled}
                          onChange={(e) => handlePreferenceChange('news_updates_enabled', e.target.checked)}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {t('notifications.categories.newsUpdates')}
                          <Chip label="Future" size="small" variant="outlined" color="info" />
                        </Box>
                      }
                    />
                  </FormGroup>
                </CardContent>
              </Card>
            </MotionDiv>
          </Grid>
          
          {/* Schedule & Priority Settings */}
          <Grid item xs={12} md={6}>
            <MotionDiv
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <Card>
                <CardHeader
                  avatar={<Schedule color="primary" />}
                  title={t('notifications.settings.schedule')}
                  subheader={t('notifications.settings.scheduleDesc')}
                />
                <CardContent>
                  <FormGroup sx={{ mb: 3 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={preferences.high_priority_only}
                          onChange={(e) => handlePreferenceChange('high_priority_only', e.target.checked)}
                        />
                      }
                      label={t('notifications.settings.highPriorityOnly')}
                    />
                    
                    <FormControlLabel
                      control={
                        <Switch
                          checked={preferences.weekend_notifications}
                          onChange={(e) => handlePreferenceChange('weekend_notifications', e.target.checked)}
                        />
                      }
                      label={t('notifications.settings.weekendNotifications')}
                    />
                  </FormGroup>
                  
                  <Typography variant="subtitle2" gutterBottom>
                    {t('notifications.settings.quietHours')}
                  </Typography>
                  
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={6}>
                      <MuiTimePicker
                        label={t('notifications.settings.quietStart')}
                        value={preferences.quiet_hours_start}
                        onChange={(newValue) => handlePreferenceChange('quiet_hours_start', newValue)}
                        renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <MuiTimePicker
                        label={t('notifications.settings.quietEnd')}
                        value={preferences.quiet_hours_end}
                        onChange={(newValue) => handlePreferenceChange('quiet_hours_end', newValue)}
                        renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                      />
                    </Grid>
                  </Grid>
                  
                  {preferences.quiet_hours_start && preferences.quiet_hours_end && (
                    <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
                      {t('notifications.settings.quietHoursActive', {
                        start: preferences.quiet_hours_start.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}),
                        end: preferences.quiet_hours_end.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})
                      })}
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </MotionDiv>
          </Grid>
          
          {/* Digest Settings */}
          <Grid item xs={12} md={6}>
            <MotionDiv
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <Card>
                <CardHeader
                  title={t('notifications.settings.digest')}
                  subheader={t('notifications.settings.digestDesc')}
                />
                <CardContent>
                  <FormGroup sx={{ mb: 3 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={preferences.daily_digest_enabled}
                          onChange={(e) => handlePreferenceChange('daily_digest_enabled', e.target.checked)}
                        />
                      }
                      label={t('notifications.settings.dailyDigest')}
                    />
                    
                    <FormControlLabel
                      control={
                        <Switch
                          checked={preferences.weekly_digest_enabled}
                          onChange={(e) => handlePreferenceChange('weekly_digest_enabled', e.target.checked)}
                        />
                      }
                      label={t('notifications.settings.weeklyDigest')}
                    />
                  </FormGroup>
                  
                  {(preferences.daily_digest_enabled || preferences.weekly_digest_enabled) && (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        {t('notifications.settings.digestTime')}
                      </Typography>
                      <MuiTimePicker
                        label={t('notifications.settings.preferredTime')}
                        value={new Date(preferences.digest_time)}
                        onChange={(newValue) => handlePreferenceChange('digest_time', newValue?.getTime())}
                        renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                      />
                    </Box>
                  )}
                </CardContent>
              </Card>
            </MotionDiv>
          </Grid>
        </Grid>
        
        {/* Success/Error Messages */}
        <Snackbar
          open={success}
          autoHideDuration={6000}
          onClose={() => setSuccess(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity="success" onClose={() => setSuccess(false)}>
            {t('notifications.settings.saveSuccess')}
          </Alert>
        </Snackbar>
        
        <Snackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={() => setError(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default NotificationSettings;